import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Label, Row, Table } from 'reactstrap';
import { InputField } from 'src/components';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import { setFromErrors } from 'src/helpers';
import {
    useCreateRotationTrainingMutation,
    useDeleteRotationTrainingMutation,
    useGetRotationTrainingQuery,
    useUpdateRotationTrainingMutation,
} from 'src/rtk/features/rotationTraining/rotationTrainingApi';
import * as Yup from 'yup';
import loader from '../../assets/images/loader.gif';
import DeleteInformationModal from 'src/components/DeleteInformation/DeleteInformationModal';

const initialValues: RotationTraining = {
    department: '',
    week: '',
    from_date: '',
    to_date: '',
};

const departmentOptions = [
    'Dept. of Surgery',
    'Dept. of Neonatology',
    'Dept. of Rdiology',
    'Dept. of Critical Care Medicine/Anesthesia',
];

const RotationTraining = () => {
    const [editId, setEditId] = useState<string>('');
    const [modalShow, setModalShow] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>('');
    const [selectedDepartment, setSelectedDepartment] = useState<string>('');

    const { data: rotationTrainingData, isLoading: isListLoading } = useGetRotationTrainingQuery();

    const [createRotationTraining, { isSuccess: isCreateSuccess, isError: isCreateError, error: createError, isLoading: isCreateLoading }] =
        useCreateRotationTrainingMutation();
    const [updateRotationTraining, { isSuccess: isUpdateSuccess, isError: isUpdateError, error: updateError, isLoading: isUpdateLoading }] =
        useUpdateRotationTrainingMutation();
    const [deleteRotationTraining, { isSuccess: isDeleteSuccess, isError: isDeleteError, error: deleteError }] =
        useDeleteRotationTrainingMutation();

    const err: CustomError = (createError || updateError || deleteError) as CustomError;

    const {
        handleSubmit,
        getFieldProps,
        values,
        touched,
        errors,
        resetForm,
        setErrors,
        setValues,
        setFieldValue,
        setFieldTouched,
    } = useFormik({
        initialValues,

        validationSchema: Yup.object({
            department: Yup.string().required('Department is Required!'),
            week: Yup.string().required('Week is Required!'),
            from_date: Yup.string().required('From Date is Required!'),
            to_date: Yup.string().required('To Date is Required!'),
        }),

        onSubmit: (values) => {
            if (editId) {
                updateRotationTraining({
                    id: editId,
                    data: values,
                });
            } else {
                createRotationTraining(values);
            }
        },
    });

    useEffect(() => {
        if (isCreateSuccess) {
            toast.success('Rotation Training Added Successfully!');
            resetForm();
            setSelectedDepartment('');
        }
        if (isCreateError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err?.data, setErrors);
        }
    }, [isCreateSuccess, isCreateError]);

    useEffect(() => {
        if (isUpdateSuccess) {
            toast.success('Rotation Training Updated Successfully!');
            resetForm();
            setEditId('');
            setSelectedDepartment('');
        }
        if (isUpdateError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err?.data, setErrors);
        }
    }, [isUpdateSuccess, isUpdateError]);

    useEffect(() => {
        if (isDeleteSuccess) {
            toast.success('Rotation Training Deleted Successfully!');
        }
        if (isDeleteError) {
            toast.error(err?.message || 'Something Went Wrong!');
        }
    }, [isDeleteSuccess, isDeleteError]);

    const handleEdit = (item: RotationTraining) => {
        setEditId(item?.id!);
        setSelectedDepartment(departmentOptions.includes(item.department) ? item.department : 'Others');
        setValues({
            department: item.department,
            week: item.week,
            from_date: item.from_date,
            to_date: item.to_date,
        });
    };

    const handleDelete = (modalState: boolean, id: string) => {
        setModalShow(modalState);
        setDeleteId(id);
    };

    const handleCancelEdit = () => {
        setEditId('');
        resetForm();
        setSelectedDepartment('');
    };

    return (
        <div className='page-content'>
            {(isCreateLoading || isUpdateLoading) ? (
                <div className="overlay">
                    <img src={loader} alt="Loading" height={100} width={300} />
                </div>
            ) : null}
            <Container fluid>
                <div className='page-breadcrumb-wrapper'>
                    <BetterBreadcrumb
                        breadcrumbItem={[{ link: '#', name: 'Logbook' }]}
                        title={'Summary of Training Rotation'}
                    />
                </div>
                <Card>
                    <CardBody>
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            onReset={() => {
                                handleCancelEdit();
                            }}
                        >
                            <Row>
                                <Col sm={6}>
                                    <FormGroup>
                                        <Label for="department" className="form-label required-field">Department</Label>
                                        <InputField
                                            type="select"
                                            options={[
                                                ...departmentOptions.map((department) => ({
                                                    value: department,
                                                    label: department,
                                                })),
                                                { value: 'Others', label: 'Others' },
                                            ]}
                                            {...getFieldProps('department')}
                                            value={selectedDepartment}
                                            onBlur={() => undefined}
                                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                                const department = event.target.value;
                                                setSelectedDepartment(department);
                                                setFieldValue('department', department === 'Others' ? '' : department, false);
                                                setFieldTouched('department', false, false);
                                            }}
                                            invalid={{
                                                errors: selectedDepartment ? {} : errors,
                                                touched,
                                            }}
                                        />
                                        {selectedDepartment === 'Others' ? (
                                            <div className="mt-2">
                                                <InputField
                                                    type="text"
                                                    placeholder="Enter Department"
                                                    {...getFieldProps('department')}
                                                    invalid={{ errors, touched }}
                                                />
                                            </div>
                                        ) : null}
                                    </FormGroup>
                                </Col>

                                <Col sm={6}>
                                    <FormGroup>
                                        <Label for="week" className="form-label required-field">Week</Label>
                                        <InputField
                                            type="select"
                                            placeholder="Enter Week"
                                            options={[
                                                { value: '4 Weeks', label: '4 Weeks' },
                                                { value: '3 Weeks', label: '3 Weeks' },
                                                { value: '2 Weeks', label: '2 Weeks' },
                                                { value: '1 Week', label: '1 Week' },
                                            ]}
                                            {...getFieldProps('week')}
                                            invalid={{ errors, touched }}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col sm={6}>
                                    <FormGroup>
                                        <Label for="from_date" className="form-label required-field">From Date</Label>
                                        <InputField
                                            type="date"
                                            {...getFieldProps('from_date')}
                                            invalid={{ errors, touched }}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col sm={6}>
                                    <FormGroup>
                                        <Label for="to_date" className="form-label required-field">To Date</Label>
                                        <InputField
                                            type="date"
                                            {...getFieldProps('to_date')}
                                            invalid={{ errors, touched }}
                                        />
                                    </FormGroup>
                                </Col>

                                <FormGroup className="mb-3 text-center">
                                    <Button type="reset" className="btn btn-danger me-2">
                                        {editId ? 'Cancel' : 'Reset'}
                                    </Button>
                                    <button type="submit" className="btn btn-success">
                                        {editId ? 'Update' : 'Submit'}
                                    </button>
                                </FormGroup>
                            </Row>
                        </Form>

                        <Row className="mt-4">
                            <Col sm={12}>
                                <Table bordered responsive>
                                    <thead>
                                        <tr className='bg-light'>
                                            <th>Department</th>
                                            <th>Week</th>
                                            <th>From Date</th>
                                            <th>To Date</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isListLoading ? (
                                            <tr>
                                                <td colSpan={5} className="text-center">Loading...</td>
                                            </tr>
                                        ) : rotationTrainingData?.data?.data?.length ? (
                                            rotationTrainingData.data.data.map((item: RotationTraining) => (
                                                <tr key={item.id}>
                                                    <td>{item.department}</td>
                                                    <td>{item.week}</td>
                                                    <td>{item.from_date}</td>
                                                    <td>{item.to_date}</td>
                                                    <td>
                                                        <Button
                                                            color="info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleEdit(item)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(true, item?.id!)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                    {modalShow && deleteId === item?.id ? (
                                                        <DeleteInformationModal
                                                            isOpen={modalShow}
                                                            toggle={() => setModalShow(!modalShow)}
                                                            id={deleteId}
                                                            deleteInfomation={deleteRotationTraining}
                                                        />
                                                    ) : null}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="text-center">No Data Found</td>
                                            </tr>
                                        )}


                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

            </Container>
        </div>
    );
};

export default RotationTraining;
